import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Collapse
} from '@mui/material';
import {
  Home as HomeIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  ContactSupport as ContactIcon,
  Gavel as GavelIcon,
  PrivacyTip as PrivacyIcon,
  LocalShipping as ShippingIcon,
  AssignmentReturn as ReturnIcon,
  Help as HelpIcon,
  ExpandLess,
  ExpandMore,
  ArrowRight as ArrowIcon,
  Map as MapIcon
} from '@mui/icons-material';
import api from '../services/api';

function SitemapPage() {
  const { t } = useTranslation('common');
  const [categories, setCategories] = useState([]);
  const [openSections, setOpenSections] = useState({
    shopping: true,
    member: true,
    info: true,
    customer: true
  });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get('/products/categories');
        if (response.data.success) {
          setCategories(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sitemapSections = [
    {
      id: 'shopping',
      title: t('sitemap.shopping'),
      icon: <StoreIcon />,
      items: [
        { label: t('sitemap.home'), path: '/', icon: <HomeIcon fontSize="small" /> },
        { label: t('sitemap.all_products'), path: '/products', icon: <StoreIcon fontSize="small" /> },
        ...categories.map(cat => ({
          label: cat.name,
          path: `/products?category=${cat.id}`,
          icon: <CategoryIcon fontSize="small" />
        })),
        { label: t('sitemap.cart'), path: '/cart', icon: <CartIcon fontSize="small" /> }
      ]
    },
    {
      id: 'member',
      title: t('sitemap.member_services'),
      icon: <PersonIcon />,
      items: [
        { label: t('sitemap.login'), path: '/login', icon: <PersonIcon fontSize="small" /> },
        { label: t('sitemap.member_center'), path: '/member', icon: <PersonIcon fontSize="small" /> },
        { label: t('sitemap.my_orders'), path: '/member', icon: <CartIcon fontSize="small" /> }
      ]
    },
    {
      id: 'info',
      title: t('sitemap.shopping_info'),
      icon: <InfoIcon />,
      items: [
        { label: t('sitemap.about_us'), path: '/shopping-guide?tab=0', icon: <InfoIcon fontSize="small" /> },
        { label: t('sitemap.shopping_flow'), path: '/shopping-guide?tab=1', icon: <HelpIcon fontSize="small" /> },
        { label: t('sitemap.payment_shipping'), path: '/shopping-guide?tab=2', icon: <ShippingIcon fontSize="small" /> },
        { label: t('sitemap.return_policy'), path: '/shopping-guide?tab=3', icon: <ReturnIcon fontSize="small" /> },
        { label: t('sitemap.terms'), path: '/shopping-guide?tab=4', icon: <GavelIcon fontSize="small" /> },
        { label: t('sitemap.privacy'), path: '/shopping-guide?tab=5', icon: <PrivacyIcon fontSize="small" /> }
      ]
    },
    {
      id: 'customer',
      title: t('sitemap.customer_service'),
      icon: <ContactIcon />,
      items: [
        { label: t('sitemap.contact_us'), path: '/contact', icon: <ContactIcon fontSize="small" /> },
        { label: t('sitemap.sitemap'), path: '/sitemap', icon: <MapIcon fontSize="small" /> }
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <MapIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {t('sitemap.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('sitemap.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {sitemapSections.map((section) => (
          <Grid item xs={12} md={6} key={section.id}>
            <Paper sx={{ overflow: 'hidden' }}>
              <ListItemButton
                onClick={() => toggleSection(section.id)}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  {section.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight="bold">
                      {section.title}
                    </Typography>
                  }
                />
                {openSections[section.id] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items.map((item, index) => (
                    <React.Fragment key={item.path + index}>
                      <ListItem
                        disablePadding
                        component={RouterLink}
                        to={item.path}
                        sx={{
                          color: 'text.primary',
                          textDecoration: 'none',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemButton sx={{ pl: 4 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.icon || <ArrowIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText primary={item.label} />
                        </ListItemButton>
                      </ListItem>
                      {index < section.items.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Collapse>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mt: 4, p: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('sitemap.not_found_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('sitemap.not_found_desc')}
          <RouterLink to="/contact" style={{ color: '#1976d2', marginLeft: 4 }}>
            {t('sitemap.contact_us')}
          </RouterLink>
        </Typography>
      </Paper>
    </Container>
  );
}

export default SitemapPage;
